/**
 * TKT-2026-106987: ONE formatter for every amount the kiosk shows.
 *
 * A yen store's kiosk printed "600.00 Yen": each screen wrote its own
 * `${symbol}${n.toFixed(2)}`, so the store's currency chose the symbol and
 * nothing else. Two decimals is the cent, and a yen, won, peso or krona price
 * has no cent to show. toFixed also never groups thousands.
 *
 * The digit count comes from Intl, the same table the pricing engine and the
 * SortSwift app round with (currencyGrid.js getPriceGridDigits): the cent, or
 * the currency's own unit when that unit is coarser than a cent (JPY, KRW, CLP,
 * ISK). Every two-decimal currency keeps its two decimals.
 *
 * The symbol is the store's, from sortswift-api's one currency list via the
 * display config (KioskThemeContext passes it as options.symbol), so a store sees
 * the same "¥" / "CLP$" / "kr" every SortSwift surface draws. Without one, the
 * sign is drawn from the code (currencySymbols.js). TKT-2026-107434
 *
 * Do not format money with toFixed anywhere else in src/. money.guard.test.js
 * fails the build on a new one.
 */
import { getCurrencySymbol } from './currencySymbols';

/** A well-formed ISO 4217 code, uppercased, or null. */
export function normalizeCurrencyCode(code) {
    if (typeof code !== 'string') return null;
    const trimmed = code.trim().toUpperCase();
    return /^[A-Z]{3}$/.test(trimmed) ? trimmed : null;
}

const digitsCache = new Map();

/**
 * Decimal places on the currency's price grid: 2 (the cent), or 0 for a
 * currency with no minor unit. An unknown or missing code keeps 2, the
 * kiosk's behaviour before the store's display config has loaded.
 */
export function getPriceGridDigits(currency) {
    const code = normalizeCurrencyCode(currency);
    if (!code) return 2;
    if (digitsCache.has(code)) return digitsCache.get(code);
    let digits = 2;
    try {
        const resolved = new Intl.NumberFormat('en-US', { style: 'currency', currency: code })
            .resolvedOptions().maximumFractionDigits;
        if (Number.isInteger(resolved) && resolved >= 0) digits = Math.min(resolved, 2);
    } catch {
        digits = 2;
    }
    digitsCache.set(code, digits);
    return digits;
}

/** True for a currency whose prices are whole units (JPY, KRW, CLP, ISK). */
export function isWholeUnitCurrency(currency) {
    return getPriceGridDigits(currency) === 0;
}

/**
 * Format an amount in the store's currency: "¥123,456", "$1,234.50", "-$5.00".
 *
 * options.symbol     the symbol to print; defaults to the sign drawn from `currency`.
 * options.extraDigit one decimal past the grid, for per-card bulk rates:
 *                    true      always ("$0.500" as before; "¥0.5" only when there is one)
 *                    'subunit' only when 0 < |amount| < one grid step ("$0.005", "¥0.5")
 */
export function formatMoney(amount, currency, { symbol, extraDigit = false } = {}) {
    const n = Number.parseFloat(amount);
    const value = Number.isFinite(n) ? n : 0;
    const digits = getPriceGridDigits(currency);
    const code = normalizeCurrencyCode(currency);
    const sign = symbol ?? (code ? getCurrencySymbol(code) : '$');

    let min = digits;
    let max = digits;
    const abs = Math.abs(value);
    const subUnit = abs > 0 && abs < 10 ** -digits;
    if (extraDigit === true || (extraDigit === 'subunit' && subUnit)) {
        max = digits + 1;
        // A cent currency always showed its bulk third decimal ("$0.500"); a
        // whole-unit one shows a fraction only when the rate really has one.
        min = digits === 0 ? 0 : max;
    }

    const body = abs.toLocaleString('en-US', {
        minimumFractionDigits: min,
        maximumFractionDigits: max,
        useGrouping: true,
    });
    // "-0" and "-¥0" are not amounts; a value that rounds to zero prints unsigned.
    const negative = value < 0 && /[1-9]/.test(body);
    return `${negative ? '-' : ''}${sign}${body}`;
}

/** The bare number on the grid, no symbol: "123,456" / "1,234.50". */
export function formatMoneyNumber(amount, currency, options = {}) {
    return formatMoney(amount, currency, { ...options, symbol: '' });
}
